import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Package, AlertTriangle, PackageX, TrendingUp, Minus, Plus, Save, Search,
  CheckCircle2, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../context/SettingsContext';

export default function AdminStock() {
  const { settings, refresh: refreshSettings } = useSettings();
  const threshold = Math.max(1, parseInt(settings?.low_stock_threshold, 10) || 5);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [edits, setEdits] = useState({}); // {productId: pendingStockValue}
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all | low | out | ok
  const [thresholdInput, setThresholdInput] = useState(threshold);
  const [savingThreshold, setSavingThreshold] = useState(false);

  useEffect(() => { setThresholdInput(threshold); }, [threshold]);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get('/products/admin'), api.get('/categories')]);
    setProducts(p.data);
    setCategories(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = products.length;
    const out = products.filter((p) => p.stock === 0).length;
    const low = products.filter((p) => p.stock > 0 && p.stock <= threshold).length;
    const ok = products.filter((p) => p.stock > threshold).length;
    const totalUnits = products.reduce((s, p) => s + (p.stock || 0), 0);
    return { total, out, low, ok, totalUnits };
  }, [products, threshold]);

  const filtered = useMemo(() => {
    return products
      .filter((p) => {
        if (catFilter !== 'all' && p.category !== catFilter) return false;
        if (statusFilter === 'low' && !(p.stock > 0 && p.stock <= threshold)) return false;
        if (statusFilter === 'out' && p.stock !== 0) return false;
        if (statusFilter === 'ok' && p.stock <= threshold) return false;
        if (search) {
          const q = search.toLowerCase();
          if (!p.name.toLowerCase().includes(q) && !p.category.toLowerCase().includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => a.stock - b.stock);
  }, [products, search, catFilter, statusFilter, threshold]);

  const quickAdjust = async (id, delta) => {
    try {
      const { data } = await api.post(`/products/${id}/stock`, { delta });
      setProducts((prev) => prev.map((p) => p.id === id ? data : p));
    } catch { toast.error('Error al ajustar stock'); }
  };

  const onManualChange = (id, value) => {
    setEdits((e) => ({ ...e, [id]: value }));
  };

  const saveManual = async (id) => {
    const raw = edits[id];
    const value = Math.max(0, parseInt(raw, 10) || 0);
    setSavingId(id);
    try {
      const { data } = await api.put(`/products/${id}/stock`, { stock: value });
      setProducts((prev) => prev.map((p) => p.id === id ? data : p));
      setEdits((e) => { const n = { ...e }; delete n[id]; return n; });
      toast.success(`${data.name}: stock actualizado a ${data.stock}`);
    } catch { toast.error('Error al guardar'); }
    finally { setSavingId(null); }
  };

  const badge = (stock) => {
    if (stock === 0) return { label: 'Agotado', cls: 'bg-rose-50 text-rose-700 border-rose-200' };
    if (stock <= threshold) return { label: 'Bajo', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    return { label: 'Disponible', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  };

  const saveThreshold = async () => {
    const value = Math.max(1, parseInt(thresholdInput, 10) || 1);
    setSavingThreshold(true);
    try {
      await api.put('/settings', { low_stock_threshold: value });
      await refreshSettings();
      toast.success(`Umbral de stock bajo: ≤${value}`);
    } catch {
      toast.error('Error al guardar el umbral');
    } finally {
      setSavingThreshold(false);
    }
  };

  return (
    <div data-testid="admin-stock-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-slate-900">Control de Stock</h1>
        <p className="text-slate-500 text-sm">Inventario en tiempo real. El stock se descuenta automáticamente cuando se envía un pedido por WhatsApp.</p>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Package} label="Total productos" value={stats.total} color="bg-pinky-50 text-pinky-600" />
        <StatCard icon={TrendingUp} label="Unidades totales" value={stats.totalUnits} color="bg-blue-50 text-blue-600" />
        <StatCard icon={AlertTriangle} label={`Stock bajo (≤${threshold})`} value={stats.low} color="bg-amber-50 text-amber-600" onClick={() => setStatusFilter('low')} />
        <StatCard icon={PackageX} label="Agotados" value={stats.out} color="bg-rose-50 text-rose-600" onClick={() => setStatusFilter('out')} />
      </div>

      {/* THRESHOLD CONFIG */}
      <div className="bg-gradient-to-r from-pinky-50 to-lilac-50 border border-pinky-200 rounded-2xl p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="stock-threshold-card">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center text-pinky-600 flex-shrink-0">
            <AlertTriangle className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 text-sm">Umbral de "Stock bajo"</p>
            <p className="text-xs text-slate-500">Los productos con stock menor o igual a este número se marcarán como bajos.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:justify-end">
          <Input
            type="number"
            min={1}
            value={thresholdInput}
            onChange={(e) => setThresholdInput(e.target.value)}
            className="h-10 w-24 rounded-xl text-center font-semibold"
            data-testid="stock-threshold-input"
          />
          <Button
            onClick={saveThreshold}
            disabled={savingThreshold || parseInt(thresholdInput, 10) === threshold}
            className="btn-pink rounded-xl h-10 px-4 text-sm gap-1.5"
            data-testid="stock-threshold-save"
          >
            <Save className="h-3.5 w-3.5" /> {savingThreshold ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o categoría…" className="rounded-xl pl-9" data-testid="stock-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="rounded-xl sm:w-48" data-testid="stock-cat-filter"><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-xl sm:w-40" data-testid="stock-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="ok">Disponibles</SelectItem>
            <SelectItem value="low">Stock bajo</SelectItem>
            <SelectItem value="out">Agotados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Producto</th>
              <th className="text-left p-3">Categoría</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-center p-3">Ajuste rápido</th>
              <th className="text-center p-3">Stock manual</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-slate-500">No hay productos que coincidan con el filtro.</td></tr>
            ) : filtered.map((p) => {
              const b = badge(p.stock);
              const editing = edits[p.id] !== undefined;
              return (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`stock-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {p.image ? (
                        <img src={p.image} alt="" className="h-11 w-11 rounded-lg object-cover" loading="lazy" decoding="async" />
                      ) : (
                        <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon className="h-4 w-4" /></div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate max-w-[240px]">{p.name}</p>
                        <p className="text-xs text-slate-500">Q{p.price}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-slate-600">{p.category}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-bold ${b.cls}`} data-testid={`stock-badge-${p.id}`}>
                      {p.stock === 0 ? <PackageX className="h-3 w-3" /> : p.stock <= threshold ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                      {p.stock} · {b.label}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => quickAdjust(p.id, -5)} className="text-xs px-2 h-7 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 font-bold" data-testid={`q-m5-${p.id}`}>-5</button>
                      <button onClick={() => quickAdjust(p.id, -1)} className="h-7 w-7 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 inline-flex items-center justify-center" data-testid={`q-m1-${p.id}`}><Minus className="h-3 w-3" /></button>
                      <button onClick={() => quickAdjust(p.id, 1)} className="h-7 w-7 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 inline-flex items-center justify-center" data-testid={`q-p1-${p.id}`}><Plus className="h-3 w-3" /></button>
                      <button onClick={() => quickAdjust(p.id, 5)} className="text-xs px-2 h-7 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-bold" data-testid={`q-p5-${p.id}`}>+5</button>
                      <button onClick={() => quickAdjust(p.id, 10)} className="text-xs px-2 h-7 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-bold" data-testid={`q-p10-${p.id}`}>+10</button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <Input
                        type="number"
                        min={0}
                        value={editing ? edits[p.id] : p.stock}
                        onChange={(e) => onManualChange(p.id, e.target.value)}
                        className="h-8 w-20 rounded-lg text-center text-sm"
                        data-testid={`stock-input-${p.id}`}
                      />
                      <Button
                        onClick={() => saveManual(p.id)}
                        disabled={!editing || savingId === p.id}
                        className="btn-pink rounded-lg h-8 px-3 text-xs"
                        data-testid={`stock-save-${p.id}`}
                      >
                        <Save className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 text-left ${onClick ? 'hover:border-pinky-300 cursor-pointer transition-colors' : 'cursor-default'}`}
    data-testid={`stock-stat-${label.toLowerCase().replace(/\s/g, '-').replace(/[^\w-]/g, '')}`}
  >
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="h-5 w-5" strokeWidth={1.8} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
      <p className="font-display text-2xl text-slate-900 leading-tight">{value}</p>
    </div>
  </button>
);
